/**
 * ROWIE — PDP tabs
 *
 * Tab switching plus the description clamp. The clamp class is applied
 * from JS rather than in the markup so that with JS disabled the full
 * description shows — a truncated description with no way to expand it
 * would be worse than no truncation at all.
 */

(function () {
  const ROOT = '[data-rowie-tabs]';
  const CLAMP_LINES = 5;

  class PdpTabs {
    constructor(root) {
      this.root = root;
      this.tabs = [...root.querySelectorAll('[data-tab]')];
      this.panels = [...root.querySelectorAll('[data-panel]')];
      this.clamp = root.querySelector('[data-tabs-clamp]');
      this.readMore = root.querySelector('[data-tabs-readmore]');

      if (this.tabs.length === 0) return;

      this.init();
    }

    init() {
      this.bindTabs();
      this.setupClamp();
      this.setupWidth();
      this.setupChevrons();
    }

    /**
     * Chevrons appear only when the row overflows, and each hides at its
     * own end of the track. State is recomputed on scroll and resize so
     * the affordance never lies about where you can go.
     */
    setupChevrons() {
      this.nav = this.root.querySelector('[data-tabs-nav]');
      this.chevPrev = this.root.querySelector('[data-tabs-chev="prev"]');
      this.chevNext = this.root.querySelector('[data-tabs-chev="next"]');

      if (!this.nav || !this.chevPrev || !this.chevNext) return;

      const EDGE = 2; // sub-pixel scroll positions never settle on exact 0

      const update = () => {
        const max = this.nav.scrollWidth - this.nav.clientWidth;
        const overflows = max > EDGE;

        this.chevPrev.hidden = !overflows || this.nav.scrollLeft <= EDGE;
        this.chevNext.hidden = !overflows || this.nav.scrollLeft >= max - EDGE;
      };

            /**
       * Snap to the next tab that isn't fully in view rather than moving a
       * fixed fraction of the row. A fixed step leaves you stranded a few
       * px short of the start on the way back, because the return journey
       * rarely divides evenly into the outbound one — and it cuts labels
       * in half. Aligning to tab edges always terminates cleanly at 0.
       */
      const step = (direction) => {
        const tabs = [...this.nav.querySelectorAll('[data-tab]')];
        if (tabs.length === 0) return;

        const navBox = this.nav.getBoundingClientRect();
        const chevWidth = 30; // must match .rowie-tabs__chev width
        let target = null;

        if (direction === 'next') {
          // First tab whose right edge is past the visible area.
          const limit = navBox.right - chevWidth;
          const found = tabs.find(
            (tab) => tab.getBoundingClientRect().right > limit + 1
          );
          if (found) {
            target = this.nav.scrollLeft
              + (found.getBoundingClientRect().left - navBox.left)
              - chevWidth;
          }
          } else {
          // Back always returns to the start. With five tabs there is
          // never more than one screen to come back through, so stepping
          // is just a slower route to the same place — and it was landing
          // short of the origin.
          target = 0;
        }

        if (target === null) return;

        const max = this.nav.scrollWidth - this.nav.clientWidth;
        // Snapping below the chevron's width means we were reaching for the
        // first tab — go all the way to 0 so it lands flush at its origin.
        if (target < chevWidth) target = 0;
        if (target > max) target = max;

        this.nav.scrollTo({ left: target, behavior: 'smooth' });
      };

      this.chevPrev.addEventListener('click', () => step('prev'));
      this.chevNext.addEventListener('click', () => step('next'));

      this.nav.addEventListener('scroll', update, { passive: true });

      let frame;
      window.addEventListener('resize', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      });

      update();

      // Labels can reflow once webfonts land, changing whether the row
      // overflows at all.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(update);
      }
    }

    /**
     * The tabs live in a flex/grid column whose min-width: auto lets it be
     * pushed wider than the viewport. A percentage cap is useless in that
     * case, so measure the real available width from the viewport and the
     * element's own left offset and write it to a custom property.
     */
    setupWidth() {
      const apply = () => {
        const viewport = document.documentElement.clientWidth;
        if (viewport > 749) {
          this.root.style.removeProperty('--rowie-tabs-width');
          return;
        }

        // Neutralise the existing cap before measuring, or we just
        // re-measure last frame's answer.
        this.root.style.removeProperty('--rowie-tabs-width');

        // Measure the element's own left inset and mirror it on the right.
        // Reading the parent's padding-right misses insets that come from
        // a margin or an ancestor, which is how the overhang crept back in.
        const left = this.root.getBoundingClientRect().left;
        const available = Math.max(viewport - (left * 2), 0);

        this.root.style.setProperty('--rowie-tabs-width', available + 'px');
      };

      apply();

      let frame;
      this._onResize = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(apply);
      };
      window.addEventListener('resize', this._onResize);
      window.addEventListener('orientationchange', this._onResize);
    }

        bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.tab));
      });
    }

    /**
     * Bring a newly selected tab fully into view. scrollIntoView is no good
     * here — it ignores the chevrons, so a tab can end up flush to the edge
     * and still sit underneath one. Measuring against the chevron inset
     * keeps the label clear of the glyph.
     */
      revealTab(tab) {
      if (!this.nav || !tab) return;
      const CHEV = 30; // must match .rowie-tabs__chev width
      const PAD = 8;

      const navBox = this.nav.getBoundingClientRect();
      const tabBox = tab.getBoundingClientRect();

      // Only inset on the side a chevron is actually showing.
      const padLeft = this.chevPrev && !this.chevPrev.hidden ? CHEV : 0;
      const padRight = this.chevNext && !this.chevNext.hidden ? CHEV : 0;

      if (tabBox.left < navBox.left + padLeft) {
        this.nav.scrollLeft -= navBox.left + padLeft - tabBox.left + PAD;
      } else if (tabBox.right > navBox.right - padRight) {
        this.nav.scrollLeft += tabBox.right - (navBox.right - padRight) + PAD;
      }
    }

    select(name) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.tab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) this.revealTab(tab);
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== name;
      });

      // The content column may be sticky and height-aware.
      this.root.dispatchEvent(
        new CustomEvent('product-content-height-change', { bubbles: true })
      );
    }

    setupClamp() {
      if (!this.clamp || !this.readMore) return;

      // Measure unclamped, then clamp only if it actually overflows —
      // otherwise a short description gets a pointless "read more".
      const style = window.getComputedStyle(this.clamp);
      const lineHeight = parseFloat(style.lineHeight);
      const maxHeight = lineHeight * CLAMP_LINES;

      if (!lineHeight || this.clamp.scrollHeight <= maxHeight + 1) return;

      this.clamp.classList.add('is-clamped');
      this.readMore.hidden = false;

      this.readMore.addEventListener('click', () => {
        const clamped = this.clamp.classList.toggle('is-clamped');
        this.readMore.textContent = clamped
          ? this.readMore.dataset.more || 'Read more'
          : this.readMore.dataset.less || 'Read less';
        this.root.dispatchEvent(
          new CustomEvent('product-content-height-change', { bubbles: true })
        );
      });

      this.readMore.dataset.more = this.readMore.textContent.trim();
      this.readMore.dataset.less = this.readMore.dataset.less || 'Read less';
    }
  }

  function initAll() {
    document.querySelectorAll(ROOT).forEach((root) => {
      if (root.dataset.initialised) return;
      root.dataset.initialised = 'true';
      new PdpTabs(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll(ROOT).forEach((root) => {
      root.removeAttribute('data-initialised');
    });
    initAll();
  });
})();